import { instance } from 'utils/axios.instance';
import {
  ICreateFileLinkBody,
  ICreateFileLinkResponse,
  ICreateMediaEntryBody,
  ICreateMediaEntryResponse,
  IGetProjectMediaResponse,
  IResolvedMediaItem,
  IResolveFileCabinetIdsResponse,
} from 'redux-store/api/file-cabinet';

/**
 * Imperative service helpers for the unified File Cabinet API.
 *
 * These wrap the existing axios instance so they can be called from
 * Jotai atoms (which don't have access to React's RTK Query hooks).
 * RTK Query hooks remain available for React components.
 */

const FILE_CABINET = '/file-cabinet';

export async function createMediaEntry(
  body: ICreateMediaEntryBody,
): Promise<ICreateMediaEntryResponse> {
  const { projectId, ...payload } = body;
  return instance.post(`${FILE_CABINET}/${projectId}/media-entry`, payload);
}

export async function deleteMediaEntry(
  projectId: string | number,
  entryId: number,
  contextId?: string,
): Promise<void> {
  const url = contextId
    ? `${FILE_CABINET}/${projectId}/media-entry/${entryId}?contextId=${encodeURIComponent(contextId)}`
    : `${FILE_CABINET}/${projectId}/media-entry/${entryId}`;
  return instance.delete(url);
}

export async function deleteMediaEntriesByPost(
  projectId: string | number,
  contextId: string,
): Promise<void> {
  return instance.delete(
    `${FILE_CABINET}/${projectId}/media-entries-by-post/${contextId}`,
  );
}

export async function getProjectMedia(
  projectId: string | number,
  page = 1,
  limit = 50,
): Promise<IGetProjectMediaResponse> {
  return instance.get(`${FILE_CABINET}/${projectId}/media?page=${page}&limit=${limit}`);
}

export async function resolveFileCabinetIds(
  projectId: string | number,
  ids: number[],
): Promise<IResolveFileCabinetIdsResponse> {
  if (ids.length === 0) {
    return { items: [] as IResolvedMediaItem[] };
  }
  return instance.post(`${FILE_CABINET}/${projectId}/resolve`, { ids });
}

export async function createFileLink(
  body: ICreateFileLinkBody,
): Promise<ICreateFileLinkResponse> {
  const { projectId, ...payload } = body;
  return instance.post(`${FILE_CABINET}/${projectId}/file-link`, payload);
}

export async function updateMediaEntryDescription(
  projectId: string | number,
  entryId: number,
  description: string | null,
): Promise<void> {
  return instance.patch(
    `${FILE_CABINET}/${projectId}/media-entry/${entryId}/description`,
    { description },
  );
}
